/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSessionStorage } from 'react-use';

import { useDeleteAttachment } from '@hooks/api/extension';
import { useUploadFile } from '@hooks/api/file';
import { useCuration } from '@hooks/useCuration';
import { useMultiUploadProgress } from '@hooks/useMultiUploadProgress';
import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { useGetS3AssetRecordLazyQuery } from 'generated/file.generated';
import type { Attachment } from 'generated/types';

const messages = defineMessages({
  fetchError: {
    id: 'attachments.fetchError',
    defaultMessage: 'Fetch Error',
  },
  postError: {
    id: 'attachments.postError',
    defaultMessage: 'Failed to upload attachment(s)',
  },
  deleteError: {
    id: 'attachments.deleteError',
    defaultMessage: 'Failed to delete attachment(s)',
  },
  downloadError: {
    id: 'attachments.downloadError',
    defaultMessage: 'Failed to download attachment',
  },
});

export interface FileManagementProps {
  /** Patient ID that the attachments belong to */
  patient_id?: string | null;
  /** Fhir resource ID of the resource with attachments. Not required for download. */
  resource?: string;
  /** Existing files  */
  attachments?: Attachment[];
}

export interface FileManagementHelpers {
  progress: number | null;
  deleting: boolean;
  operating: boolean[];
  handleDownload: (
    doc: Pick<Attachment, 'id' | 'title'>,
    index: number
  ) => Promise<void>;
  handleUpload: (files: DroppedFile[]) => Promise<void>;
  handleDelete: (id: string | null | undefined) => Promise<void>;
}

export function useFileManagement<TProps extends FileManagementProps>(
  props: TProps
) {
  const { resource, attachments = [], patient_id } = props;

  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [token] = useSessionStorage('share_token');
  const {
    state: { isAuthenticated },
  } = useSession();
  const {
    state: { isEditing },
  } = useCuration();

  const [getS3Asset] = useGetS3AssetRecordLazyQuery({
    // Only add authorization header if resource is being shared (not authenticated)
    ...(!isAuthenticated && {
      context: {
        headers: {
          authorization: token,
        },
      },
    }),
  });
  const [{ uploadAttachment }] = useUploadFile();
  const [deleteAttachment] = useDeleteAttachment();
  const {
    progress,
    setNumFiles,
    setUploadProgress,
    setUploadedFiles,
    setChunkSize,
    reset,
  } = useMultiUploadProgress();

  const [operating, setOperating] = useState<boolean[]>(
    attachments.map(() => false)
  );
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isEditing) {
      setOperating(attachments.map(() => false));
    }
  }, [attachments, isEditing]);

  const setOperatingAtIndex = (acting: boolean, index: number) => {
    const newValue = [...operating];
    newValue[index] = acting;
    setOperating(newValue);
  };

  const handleDownload: FileManagementHelpers['handleDownload'] = async (
    doc,
    index
  ) => {
    if (!doc.id || !patient_id) {
      return;
    }
    setOperatingAtIndex(true, index);
    let url: string | null | undefined;
    try {
      const response = await getS3Asset({
        variables: {
          input: {
            record_id: doc.id,
            patient_id,
          },
        },
        fetchPolicy: 'no-cache',
      });
      url = response.data?.getS3AssetRecord.presignedGetUrl;
      if (!url) {
        throw new Error('Unable to download file');
      }
    } catch (error) {
      enqueueSnackbar({
        message: formatMessage(messages.downloadError),
        variant: 'error',
      });
    } finally {
      setOperatingAtIndex(false, index);
    }
    if (url) {
      const a = document.createElement('A') as HTMLAnchorElement;
      a.href = url;
      a.download = '';
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const uploadFile = async (file: File) => {
    if (!resource) {
      return;
    }
    try {
      await uploadAttachment(resource, file, (progress) =>
        setUploadProgress(progress)
      );
    } catch (error) {
      enqueueSnackbar({ message: formatMessage(messages.postError) });
    }
  };

  const handleUpload: FileManagementHelpers['handleUpload'] = async (files) => {
    setNumFiles(files.length);
    setChunkSize(1);
    for (const file of files) {
      // TODO Replace with more efficient logic
      // eslint-disable-next-line no-await-in-loop
      await uploadFile(file);
      setUploadedFiles((prev) => prev + 1);
      setUploadProgress(0);
    }
    reset();
  };

  const handleDelete: FileManagementHelpers['handleDelete'] = async (id) => {
    if (!resource || !id) {
      return;
    }
    setDeleting(true);
    try {
      await deleteAttachment({
        input: {
          id,
          resource,
        },
      });
    } catch (error) {
      enqueueSnackbar({ message: formatMessage(messages.deleteError) });
    } finally {
      setDeleting(false);
    }
  };

  const helpers: FileManagementHelpers = {
    deleting,
    progress,
    operating,
    handleDelete,
    handleDownload,
    handleUpload,
  };

  return helpers;
}
